/** How long toast notifications stay on screen. Applied as a multiplier over each toast's
 *  own base duration; 'regular' is the explicit ×1 middle option, 'persistent' means never
 *  auto-dismiss (dismiss on click). 'system' also renders at ×1 but means "no stored override". */
export type ToastDurationPreset = 'system' | 'short' | 'regular' | 'long' | 'persistent';

export const TOAST_DURATION_KEY = 'display.toastDuration';

/** Anything that isn't a known preset resolves to the default, 'system'. */
export function coerceToastDuration(v: unknown): ToastDurationPreset {
  return v === 'short' || v === 'regular' || v === 'long' || v === 'persistent' ? v : 'system';
}

/** Multiplier for a preset, or null when the preset means "no auto-dismiss". */
export function factorFor(preset: ToastDurationPreset): number | null {
  switch (preset) {
    case 'short':
      return 0.5;
    case 'long':
      return 2;
    case 'persistent':
      return null;
    case 'regular':
      return 1;
    default:
      return 1;
  }
}

/** Scale a toast's base duration by the active preset.
 *  - base `undefined` (error toast) -> `undefined` (stays sticky under every preset)
 *  - persistent -> `undefined` (never auto-dismiss)
 *  - otherwise  -> round(base * factor) */
export function scaleDuration(
  base: number | undefined,
  preset: ToastDurationPreset,
): number | undefined {
  if (base == null) return undefined;
  const factor = factorFor(preset);
  return factor === null ? undefined : Math.round(base * factor);
}
